import React, { useEffect, useRef, useState } from 'react';
import { FormLabel, Icon } from '@chakra-ui/core';
import { apiV1 } from '../common/api/api';
import { QuoteDocumentsAction } from '../common/components/QuoteDocumentsAction';
import { initials, questionDelay, questionState, questionStateLabels } from './questionHelpers';
import './QuestionInterface.css';
import './QuestionLoading.css';

const QuoteQuestionDetails = ({
  question,
  folderId,
  onChange,
  initialResponse = '',
  onDraftChange,
}) => {
  const [response, setResponse] = useState(initialResponse);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);
  const submitting = useRef(false);
  const mounted = useRef(true);
  const state = questionState(question);
  const closed = state === 'processed';
  const delay = questionDelay(question);
  const url = `/folders/${encodeURIComponent(folderId)}/quote_tickets/${encodeURIComponent(
    question.id,
  )}`;
  const inputId = `question-response-${question.id}`;

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const refreshQuestion = async () => {
    try {
      const { data } = await apiV1.get(url);
      if (!mounted.current) return;
      onChange(data.quoteTicket || data.quote_ticket);
      setError('');
      setNotice('Cette question a déjà été traitée. La réponse a été actualisée.');
    } catch (refreshError) {
      if (mounted.current)
        setError('Cette question a déjà été traitée. Actualisez-la pour consulter la réponse.');
    }
  };

  const submitResponse = async event => {
    event.preventDefault();
    if (!response.trim() || submitting.current || conflict) return;

    submitting.current = true;
    setSaving(true);
    setError('');
    try {
      const { data } = await apiV1.patch(url, { quoteTicket: { response: response.trim() } });
      if (!mounted.current) return;
      onChange(data.quoteTicket || data.quote_ticket);
      setResponse('');
      if (onDraftChange) onDraftChange('');
      setNotice('Votre réponse est enregistrée. La question est traitée.');
    } catch (saveError) {
      if (!mounted.current) return;
      if (saveError.response?.status === 409) {
        setConflict(true);
        await refreshQuestion();
      } else {
        setError(
          'La réponse n’a pas pu être enregistrée. Votre texte est conservé, vous pouvez réessayer.',
        );
      }
    } finally {
      submitting.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <section className="qq-detail" aria-label="Détail de la question">
      <div className="qq-detail-top">
        <span className="qq-detail-reference">
          Question <span>#{question.id}</span>
        </span>
        <span className="qq-detail-actions">
          <QuoteDocumentsAction
            lastQuoteUploaded={question.lastQuoteUploaded}
            ergoReport={question.ergoReport}
          />
          <span
            className={`qq-status qq-status-${state}${delay ? ` ${delay.className}` : ''}`}
            title={delay?.label}
          >
            <Icon name={closed ? 'check' : 'comment'} size="11px" aria-hidden="true" />
            {questionStateLabels[state]}
          </span>
        </span>
      </div>
      <section className="qq-message qq-message-question" aria-label="Question de l’entreprise">
        <div className="qq-message-heading">
          <Icon name="comment" size="16px" aria-hidden="true" />
          <h2>Question de l’entreprise</h2>
        </div>
        <div className="qq-bubble">{question.question}</div>
        <div className="qq-message-author">
          <span className="qq-avatar">{initials(question.createdBy?.fullName)}</span>
          <div>
            <div className="qq-author-name">
              {question.createdBy?.fullName || 'Auteur de la question'}
            </div>
            <div className="qq-author-date">
              Soumise le {question.createdAt || question.created_at || '—'}
            </div>
          </div>
        </div>
      </section>
      {notice && (
        <div className="qq-notice" role="status">
          <Icon name="check" aria-hidden="true" />
          {notice}
        </div>
      )}
      {error && (
        <div className="qq-notice qq-error" role="alert">
          <Icon name="warning" aria-hidden="true" />
          {error}
        </div>
      )}
      {question.response?.trim() && (
        <section className="qq-message qq-message-response" aria-label="Réponse de BelAvie">
          <div className="qq-message-heading">
            <Icon name="check" size="14px" aria-hidden="true" />
            <h2>Réponse de BelAvie</h2>
          </div>
          <div className="qq-bubble">{question.response}</div>
          {(question.respondedAt || question.respondedBy?.fullName) && (
            <div className="qq-message-author">
              <div className="qq-author-date">
                {question.respondedAt ? `Le ${question.respondedAt}` : ''}
                {question.respondedBy?.fullName ? ` — ${question.respondedBy.fullName}` : ''}
              </div>
            </div>
          )}
        </section>
      )}
      {question.canRespond && !closed && (
        <form
          className={`qq-composer${response.trim() ? ' qq-composer-filled' : ''}`}
          onSubmit={submitResponse}
        >
          <div className="qq-composer-heading">
            <FormLabel htmlFor={inputId}>
              <Icon name="edit" color="coral.700" size="14px" aria-hidden="true" />
              Votre réponse
            </FormLabel>
            <span className="qq-composer-hint">
              {response ? 'Brouillon · non envoyé' : 'À l’attention de l’entreprise'}
            </span>
          </div>
          <textarea
            className="qq-textarea"
            id={inputId}
            value={response}
            onChange={event => {
              setResponse(event.target.value);
              if (onDraftChange) onDraftChange(event.target.value);
            }}
            onKeyDown={event => {
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') submitResponse(event);
            }}
            placeholder="Apportez une réponse claire à l’entreprise…"
            disabled={saving || conflict}
            aria-describedby={`${inputId}-help`}
          />
          <div className="qq-composer-tools">
            <span>
              <Icon name="lock" size="11px" aria-hidden="true" /> Votre texte reste privé jusqu’à
              l’envoi
            </span>
            <span className="qq-shortcut">
              <kbd>Ctrl / ⌘</kbd> + <kbd>Entrée</kbd> pour envoyer
            </span>
          </div>
          <div className="qq-composer-footer">
            <p id={`${inputId}-help`} className="qq-composer-help">
              <Icon name="envelope" size="13px" aria-hidden="true" />
              <span>
                La réponse est définitive et clôture la question. L’auteur recevra un mail pour la
                consulter dans BAM.
              </span>
            </p>
            <button
              className="qq-button qq-button-primary"
              type="submit"
              disabled={!response.trim() || saving || conflict}
              aria-busy={saving}
            >
              {saving && <span className="qq-spinner" aria-hidden="true" />}
              {saving ? 'Enregistrement…' : 'Répondre et clôturer'}
              {!saving && <Icon name="arrow-forward" size="14px" aria-hidden="true" />}
            </button>
          </div>
        </form>
      )}
      {conflict && !closed && (
        <button className="qq-button" type="button" onClick={refreshQuestion}>
          Actualiser la question
        </button>
      )}
    </section>
  );
};

export default QuoteQuestionDetails;
